// src/services/brand.service.ts
import axiosInstance from "@/config/axios/api-client.config";
import { handleApiRequest } from "@/lib/utils";

import {
  BaseApiResponse,
  ThreadCampaign,
  ThreadCampaignUpdate,
  ThreadDetails,
} from "@/types/types";
import axios from "@/config/axios/api-client.config";
import { BrandResponse, ThreadFileResponse } from "@/types/brand.types";

export async function updateCampaignMoodboard(
  brandId: string,
  campaignId: string,
  moodboardId: string,
  moodboardData: Record<string, unknown>
): Promise<BrandResponse> {
  return handleApiRequest<BrandResponse>(
    axiosInstance.put(
      `/brands/${brandId}/campaign/${campaignId}/moodboard/${moodboardId}`,
      {
        ...moodboardData,
      }
    )
  );
}

export async function deleteCampaignMoodboard(
  brandId: string,
  campaignId: string,
  moodboardId: string
): Promise<BrandResponse> {
  return handleApiRequest<BrandResponse>(
    axiosInstance.delete(
      `/brands/${brandId}/campaign/${campaignId}/moodboard/${moodboardId}`
    )
  );
}

export const uploadThreadFile = async (
  brandId: string,
  file: File,
  purpose: string = "user_data"
): Promise<ThreadFileResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("purpose", purpose);

    const response = await axios.post<BaseApiResponse<ThreadFileResponse>>(
      `/brands/${brandId}/thread-files`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    const { status_code, message, data } = response.data;

    if (status_code === 201 && data) {
      return data;
    } else {
      throw new Error(message || "Failed to upload thread file.");
    }
  } catch (error: any) {
    console.error("Error uploading thread file:", error);
    throw new Error(
      error.response?.data?.message || "Unexpected error while uploading file."
    );
  }
};

export async function deleteA2iImage(
  brand_id: string,
  image_id: string
): Promise<{ brand_id: string; image_id: string }> {
  return handleApiRequest<{ brand_id: string; image_id: string }>(
    axiosInstance.delete(`/brands/${brand_id}/a2i/images/${image_id}`)
  );
}

export async function updateBrand(
  brandId: string,
  brandData: Record<string, any>
): Promise<ThreadDetails> {
  return handleApiRequest<ThreadDetails>(
    axiosInstance.patch(`/brands/${brandId}`, brandData)
  );
}

export async function updateBrandSocialMediaField(
  brandId: string,
  field: keyof NonNullable<
    NonNullable<ThreadDetails["brand_information"]>["static"]
  >["social_media"],
  value: string
): Promise<ThreadDetails> {
  const payload = {
    [`brand_information.static.social_media.${field}`]: value,
  };

  return updateBrand(brandId, payload);
}

export async function createCampaign(
  brandId: string,
  campaignData: Omit<ThreadCampaign, "id">
): Promise<ThreadCampaign> {
  return handleApiRequest<ThreadCampaign>(
    axiosInstance.post(`/brands/${brandId}/campaign`, campaignData)
  );
}

export async function updateCampaign(
  brandId: string,
  campaignId: string,
  campaignData: Partial<ThreadCampaign>
): Promise<ThreadCampaign> {
  // console.log("Updating campaign with data:", campaignData);
  return handleApiRequest<ThreadCampaign>(
    axiosInstance.put(`/brands/${brandId}/campaign/${campaignId}`, campaignData)
  );
}

export async function patchCampaign(
  brandId: string,
  campaignId: string,
  campaignData: Partial<ThreadCampaignUpdate>
): Promise<ThreadCampaign> {
  return handleApiRequest<ThreadCampaign>(
    axiosInstance.patch(
      `/brands/${brandId}/campaign/${campaignId}`,
      campaignData
    )
  );
}

export async function updateCampaignName(
  brandId: string,
  campaignId: string,
  title: string
): Promise<ThreadCampaign> {
  return updateCampaign(brandId, campaignId, {
    campaign: { title },
  });
}

export async function deleteCampaign(
  brandId: string,
  campaignId: string
): Promise<void> {
  return handleApiRequest<void>(
    axiosInstance.delete(`/brands/${brandId}/campaign/${campaignId}`)
  );
}

export async function addDeprioritizedIds(
  brandId: string,
  campaignId: string,
  moodboardId: string,
  deprioritizedIds: string[]
): Promise<void> {
  return handleApiRequest<void>(
    axiosInstance.post(
      `/brands/${brandId}/campaign/${campaignId}/deprioritized-ids`,
      {
        brand_id: brandId,
        campaign_id: campaignId,
        deprioritized_ids: deprioritizedIds,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
  );
}
export async function setCampaignCuration(
  brandId: string,
  campaignId: string,
  isCuratedForBrand: boolean
): Promise<ThreadCampaign> {
  return handleApiRequest<ThreadCampaign>(
    axiosInstance.post(`/brands/${brandId}/campaign/${campaignId}/curation`, {
      is_curated_for_brand: isCuratedForBrand,
    })
  );
}
/**
 * Delete a brand permanently (Admin only).
 * Cascade deletes all campaigns and moodboards.
 */
export async function deleteBrand(brandId: string): Promise<void> {
  return handleApiRequest<void>(axiosInstance.delete(`/brands/${brandId}`));
}

/**
 * Trigger Brand Brain Analysis for a campaign or brand
 */
export interface BrandBrainAnalysisRequest {
  brand_id?: string;
  campaign_id?: string;
  batch_size?: number;
  curated_only?: boolean;
}

export interface BrandBrainAnalysisResponse {
  job_execution_id: string;
  mode: string;
  brand_id?: string;
  campaign_id?: string;
  batch_size: number;
  curated_only: boolean;
  status: string;
}

export async function triggerBrandBrainAnalysis(
  params: BrandBrainAnalysisRequest
): Promise<BrandBrainAnalysisResponse> {
  return handleApiRequest<BrandBrainAnalysisResponse>(
    axiosInstance.post(`/brands/brain-analysis/trigger`, params)
  );
}

/**
 * Duplicate a campaign with all its gallery items
 */
export interface DuplicateCampaignResponse {
  campaign: ThreadCampaign;
}

export async function duplicateCampaign(
  brandId: string,
  campaignId: string
): Promise<DuplicateCampaignResponse> {
  return handleApiRequest<DuplicateCampaignResponse>(
    axiosInstance.post(`/brands/${brandId}/campaign/${campaignId}/duplicate`)
  );
}
